<script lang="ts">
  import clsx from 'clsx';
  import { User } from '@athena-lib/Icons';
  import { Link } from '@athena-lib/Link';
  import { Image } from '@athena-lib/Image';
  import ShoppingCartWithCount from '@athena-lib/ShoppingCartWithCount/ShoppingCartWithCount.svelte';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import type { ClickEvent, ClickTarget } from '@athena-lib/types';
  import { slide } from 'svelte/transition';
  import Drawer from '../Drawer/Drawer.svelte';
  import DrawerButton from '../DrawerButton/DrawerButton.svelte';
  import Menu from '../Menu/Menu.svelte';
  import InfoBanner from '../InfoBanner/InfoBanner.svelte';
  import type { InfoBanner as InfoBannerType } from '../InfoBanner/types';
  import DrawerMenu from '../Drawer/DrawerMenu.svelte';
  import SearchBar from '../SearchBar/SearchBar.svelte';
  import SearchButton from '../SearchBar/SearchButton.svelte';
  import NewMenu from '../Menu/NewMenu.svelte';
  import { ShippingBanner } from '..';
  import type { HeaderProps } from './types';
  import { useScroll } from './useScroll.svelte';

  const {
    desktopMenuItems,
    desktopDrawerMainMenuItems,
    desktopDrawerSecondaryMenuItems,
    desktopDrawerTertiaryMenuItems,
    mobileMainMenuItems,
    mobileSecondaryMenuItems,
    mobileTertiaryMenuItems,
    stickerAppLogoUrl,
    transparentAtTop,
    signInLinkText,
    accountLinkText,
    reorderLinkText,
    infoBanners,
    dismissInfoBanner,
    currentUrl,
    drawerIsOpen,
    openDrawer,
    closeDrawer,
    searchQuery,
    setSearchQuery,
    openSearch,
    closeSearch,
    onResultClicked,
    itemsInCart,
    navigate,
    onCheckoutClick,
    reorderUrl,
    useNewCheckout,
    shippingDetails,
    onAccountClick,
    checkoutSdk,
  }: HeaderProps = $props();
  let drawerTrigger = $state<ClickTarget<HTMLButtonElement> | undefined>(undefined);

  const locallyDismissedInfoBanners: string[] = $state([]);
  function dismissInfoBannerLocal(infoBanner: InfoBannerType) {
    dismissInfoBanner(infoBanner);
    locallyDismissedInfoBanners.push(infoBanner.id);
  }

  const openDrawerInternal = (e: ClickEvent<HTMLButtonElement>) => {
    drawerTrigger = e.currentTarget ?? undefined;
    openDrawer();
  };

  const restoreFocus = () => {
    if (drawerTrigger) {
      drawerTrigger.focus();
      drawerTrigger = undefined;
    }
  };

  const translations = getCommonTranslations();
  const scroll = useScroll();

  const showShippingBanner = $derived(
    useNewCheckout && (scroll.scrollDirection === 'up' || scroll.isScrolledToTop),
  );
</script>

<div
  data-testid="header"
  class={clsx(
    'w-full transition-colors duration-500',
    scroll.isScrolledToTop && transparentAtTop ? 'bg-transparent' : 'bg-st-black',
  )}
>
  {#if showShippingBanner}
    <div class="block xl:hidden" transition:slide>
      <ShippingBanner {checkoutSdk} {shippingDetails} />
    </div>
  {/if}
  {#if infoBanners}
    {#each infoBanners as { id, content } (id)}
      {#if !locallyDismissedInfoBanners.includes(id)}
        <div>
          <InfoBanner {id} {content} dismiss={dismissInfoBannerLocal} />
        </div>
      {/if}
    {/each}
  {/if}
  <SearchBar {searchQuery} {setSearchQuery} onClose={closeSearch} {onResultClicked} />
  <div class="flex justify-between">
    <div class="hidden grow px-3 py-4 xl:block">
      {#if useNewCheckout}
        <NewMenu
          {itemsInCart}
          items={desktopMenuItems}
          openDrawer={openDrawerInternal}
          {stickerAppLogoUrl}
          {currentUrl}
          {openSearch}
          {onCheckoutClick}
          {shippingDetails}
          {onAccountClick}
          {checkoutSdk}
        />
      {:else}
        <Menu
          {itemsInCart}
          items={desktopMenuItems}
          openDrawer={openDrawerInternal}
          {stickerAppLogoUrl}
          {currentUrl}
          {openSearch}
          {onCheckoutClick}
          {onAccountClick}
        />
      {/if}
      <Drawer
        isOpen={drawerIsOpen}
        onClose={closeDrawer}
        label={translations.navigation}
        variant="hamburger-menu"
        {navigate}
      >
        <DrawerMenu
          mainMenuItems={desktopDrawerMainMenuItems}
          secondaryMenuItems={desktopDrawerSecondaryMenuItems}
          tertiaryMenuItems={desktopDrawerTertiaryMenuItems}
          {closeDrawer}
          {signInLinkText}
          {accountLinkText}
          {reorderLinkText}
          {reorderUrl}
          {restoreFocus}
          {onAccountClick}
        />
      </Drawer>
    </div>
    <div class="w-1/3 self-center px-medium xl:hidden">
      <div class="flex flex-row gap-3 text-white">
        <DrawerButton
          sizeClass="h-6 w-6"
          label={/* TODO: Can't hard code this here */ 'Toggle navigation'}
          onClick={openDrawerInternal}
        />
        <SearchButton onClick={openSearch} label="Toggle search" />
      </div>
      <Drawer
        variant="hamburger-menu"
        isOpen={drawerIsOpen}
        onClose={closeDrawer}
        label={translations.navigation}
        {navigate}
      >
        <DrawerMenu
          mainMenuItems={mobileMainMenuItems}
          secondaryMenuItems={mobileSecondaryMenuItems}
          tertiaryMenuItems={mobileTertiaryMenuItems}
          {closeDrawer}
          {signInLinkText}
          {accountLinkText}
          {reorderLinkText}
          {reorderUrl}
          {restoreFocus}
          {onAccountClick}
        />
      </Drawer>
    </div>
    <div class="flex w-1/3 self-center xl:hidden">
      <Link href="/" class="block max-h-20 p-2">
        <Image src={stickerAppLogoUrl} alt="Sticker App" class="m-auto h-full w-auto" />
      </Link>
    </div>
    <div class="flex w-1/3 justify-end gap-gutter self-center px-medium text-st-white xl:hidden">
      <button type="button" onclick={onAccountClick}>
        <User sizeClass="w-6 h-6" />
      </button>

      <div class="relative">
        <ShoppingCartWithCount
          sizeClass="h-6 w-6"
          countPromise={itemsInCart}
          onClick={onCheckoutClick}
        ></ShoppingCartWithCount>
      </div>
    </div>
  </div>
</div>
